import { useMemo, useState } from 'react'
import { StopLossEditCSS } from './StopLossEditCSS.styled'
import { onlyDigits } from 'utils/uiUtils_TS'
import { LONG_DASH } from 'constants'
import { currencyFormatter } from 'utils'
import { ASSETS } from 'constants/assets'
import { getPnL } from 'utils/pnl'
import { usePriceFeed } from 'hooks/usePriceFeed'

const INITIAL_VALUE = {
  newUserStopLossPrice: '',
  newUserStopLossValue: '',
  cancelUserStopLossOrder: false,
}

const getAssetInfo = (asset) =>
  ASSETS.filter((item) => {
    return item.id === asset
  })[0]

export const StopLossEdit = ({ order = {}, onChange }) => {
  const { userStopLossPrice, userStopLossValue, side, asset, amount, leverage, entryPrice } = order

  const { priceFeed } = usePriceFeed(asset)

  const [values, setValues] = useState(INITIAL_VALUE)
  const [isFocused, setFocusInput] = useState({})

  const onInputChange = (e) => {
    const inputName = e.target.name
    const inputValue = onlyDigits(e.target.value)
    if (inputName === 'newUserStopLossValue' && Math.abs(inputValue) >= amount) return
    setValues((prev) => {
      const newValue = { ...prev, [inputName]: inputValue }
      onChange && onChange(newValue)
      return newValue
    })
  }

  const resetValue = (propName) =>
    setValues((prev) => {
      const newValue = { ...prev, [propName]: '' }
      onChange && onChange(newValue)
      return newValue
    })

  const onInputClick = (e) => {
    const inputName = e.target.name
    if (inputName === 'newUserStopLossPrice') {
      resetValue('newUserStopLossValue')
      setFocusInput((prev) => ({ ...prev, newUserStopLossPrice: true }))
    }
    if (inputName === 'newUserStopLossValue') {
      resetValue('newUserStopLossPrice')
      setFocusInput((prev) => ({ ...prev, newUserStopLossValue: true }))
    }
  }

  const onInputBlur = (e) => {
    const inputName = e.target.name
    const inputValue = onlyDigits(e.target.value)
    if (inputName === 'newUserStopLossPrice' && (!inputValue || !values?.newUserStopLossPrice))
      setFocusInput((prev) => ({ ...prev, newUserStopLossPrice: false }))
    if (inputName === 'newUserStopLossValue' && (!inputValue || !values?.newUserStopLossValue))
      setFocusInput((prev) => ({ ...prev, newUserStopLossValue: false }))
  }

  const onCheckChange = (e) => {
    const inputName = e.target.name
    const inputCheck = e.target.checked
    setValues((prev) => {
      let prevProps = prev
      if (inputCheck) prevProps = INITIAL_VALUE
      const newValue = { ...prevProps, [inputName]: inputCheck }
      onChange && onChange(newValue)
      return newValue
    })
  }

  const lossFromPrice = useMemo(() => {
    if (!values?.newUserStopLossPrice) return ''
    const assetInfo = getAssetInfo(asset)
    const fillQuantity = (amount * leverage) / entryPrice
    const pnl = getPnL(entryPrice, values?.newUserStopLossPrice, side, fillQuantity, leverage, asset)

    if (pnl >= 0) return ''

    return currencyFormatter(Math.abs(pnl), {
      noCurrencySign: true,
      decimalDigits: assetInfo?.fiatDecimals,
    })
  }, [entryPrice, amount, leverage, values?.newUserStopLossPrice, side, asset])

  const lossFromValue = useMemo(() => {
    const assetInfo = getAssetInfo(asset)
    const stopLossValue = Math.abs(values?.newUserStopLossValue)
    const quantity = (amount * leverage) / priceFeed.price
    let stopLossPrice
    if (side === 'buy') stopLossPrice = entryPrice - stopLossValue / quantity
    else stopLossPrice = entryPrice + stopLossValue / quantity

    if (!values?.newUserStopLossValue) return ''

    if (stopLossPrice <= 0) return LONG_DASH

    return currencyFormatter(stopLossPrice, {
      noCurrencySign: false,
      decimalDigits: assetInfo?.fiatDecimals,
    })
  }, [amount, priceFeed?.price, leverage, values?.newUserStopLossValue, side, entryPrice, asset])

  const pricePlaceholder = useMemo(() => {
    if (lossFromValue) return lossFromValue
    if (isFocused?.newUserStopLossPrice) return ``
    if (!userStopLossPrice) return `${side === 'buy' ? 'Min' : 'Max'} Price`
    const assetInfo = getAssetInfo(asset)
    return currencyFormatter(userStopLossPrice, {
      noCurrencySign: false,
      decimalDigits: assetInfo?.fiatDecimals,
    })
  }, [isFocused?.newUserStopLossPrice, asset, lossFromValue, side, userStopLossPrice])

  return (
    <StopLossEditCSS>
      <div className='min'>
        <fieldset>
          <span>{`Close bid at ${side === 'buy' ? 'min' : 'max'} price`}</span>
          <input
            type='text'
            name='newUserStopLossPrice'
            // placeholder={lossFromValue? lossFromValue: userStopLossPrice || isFocused?.newUserStopLossPrice?'':`${side === 'buy' ? 'Min' : 'Max'} Price`}
            placeholder={pricePlaceholder}
            value={values?.newUserStopLossPrice || ''}
            onChange={onInputChange}
            onClick={onInputClick}
            onFocus={onInputClick}
            onBlur={onInputBlur}
            disabled={values.cancelUserStopLossOrder}
          />
        </fieldset>
        <fieldset>
          <strong>Loss</strong>
          <div className={`input_wrapper profit ${isFocused?.newUserStopLossValue ? 'active' : ''}`}>
            <input
              type='text'
              name='newUserStopLossValue'
              placeholder={
                lossFromPrice
                  ? `-${lossFromPrice}`
                  : userStopLossValue || isFocused?.newUserStopLossValue
                  ? ''
                  : LONG_DASH
              }
              className='tpsl_result_data loss'
              value={values?.newUserStopLossValue ? `-${values?.newUserStopLossValue}` || '' : ''}
              onChange={onInputChange}
              onClick={onInputClick}
              onFocus={onInputClick}
              onBlur={onInputBlur}
              disabled={values.cancelUserStopLossOrder}
            />
          </div>
        </fieldset>
      </div>
      <div className='cancel_check'>
        <input
          type='checkbox'
          name='cancelUserStopLossOrder'
          className='max_switch'
          checked={values?.cancelUserStopLossOrder}
          onChange={onCheckChange}
        />
        Cancel <strong>Stop Loss</strong> trigger
      </div>
    </StopLossEditCSS>
  )
}
